import { Component, Inject, Input, OnInit, forwardRef } from '@angular/core';
import { UIRouter }                        from '@uirouter/core';
import { TranslateService }               from '@ngx-translate/core';

import { ApiService }            from '../api/api.service';
import { BranchesLiteApiService } from '../branches/branches-lite.service';
import { CarfaxService }         from '../carfax/carfax.service';
import { CompanyService }        from '../companies/company.service';
import { ConfirmService }        from '../messages/confirm/confirm.service';
import { CustomerFactoryService } from './customer-factory.service';
import { MessagesService }       from '../messages/messages.service';
import { SessionService }        from '../sessions/session.service';
import { StreamSearchService }   from '../api/stream-search.service';
import { UKPostcodeService }     from '../uk-postcode/uk-postcode.service';
import { VVGarageFactory }      from '../../app/vv-garage/vv-garage.service';

@Component({
  selector   : 'wor-customer',
  template: require('./customer.component.html')
})
export class CustomerComponent implements OnInit {
  @Input() ams_autoclubs: any[];
  @Input() billedCustomers: any[];
  @Input() biller_balance: any;
  @Input() biller_credit_balance: any;
  @Input() companyMessages: any;
  @Input() contact_method_types: any[];
  @Input() customer: any;
  @Input() customerSources: any[];
  @Input() events: any[];
  @Input() inspections: any[];
  @Input() invoices: any[];
  @Input() payments: any[];
  @Input() paymentTerms: any[];
  @Input() price_types: any[];
  @Input() quotes: any[];
  @Input() states: any[];
  @Input() statusTypes: any[];
  @Input() taxGroups: any[];
  @Input() vehicles: any[];

  addressSearch: any;
  biller: any;
  billerSearch: any;
  branchInvoices: any[] = [];
  branchPaymentsHO: any[] = [];
  branchPaymentsStores: any[] = [];
  countryTypes: any[];
  currency: any;
  customerObj: any;
  loaded = true;
  loading = false;
  session: any;
  showCustomerDetails = true;
  showContactDetails = false;
  validations: any;

  constructor (
    @Inject(forwardRef(() => BranchesLiteApiService)) private branchesApi: BranchesLiteApiService,
    @Inject(forwardRef(() => CarfaxService)) public carfaxApi: CarfaxService,
    @Inject(forwardRef(() => UKPostcodeService)) private ukPostcodeApi: UKPostcodeService,
    @Inject(forwardRef(() => VVGarageFactory)) private vvGarageFactory: VVGarageFactory,
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => CompanyService))
    public companyService: CompanyService,
    @Inject(forwardRef(() => ConfirmService)) private confirmService: ConfirmService,
    @Inject(forwardRef(() => CustomerFactoryService))
    private customerFactoryService: CustomerFactoryService,
    @Inject(forwardRef(() => MessagesService)) private messagesService: MessagesService,
    private router: UIRouter,
    @Inject(forwardRef(() => SessionService))
    private sessionService: SessionService,
    @Inject(forwardRef(() => StreamSearchService)) private streamSearchService: StreamSearchService,
    private translateService: TranslateService
  ) {}

  ngOnInit () {
    this.session = this.sessionService.base();
    this.currency = this.sessionService.currency();

    if (!this.customer) {
      this.customer = {
        ams_autoclub: this.sessionService.company().ams_rewards_settings?.ams_default_autoclub,
        cash_or_account: 'C',
        customer_limited: false,
        customer_type: 'I',
        default_payment_terms: this.sessionService.company().default_payment_terms,
        price_type: '1'
      };
    }

    this.biller = {
      balance: this.biller_balance,
      credit_balance: this.biller_credit_balance
    };

    this.customerObj = this.customerFactoryService.create(this.customer);

    this.addressSearch = {
      list: [],
      selected: { id: '', full_address: '' },
      choose: (address, type = 'address') => {
        this.loading = true;
        return this.apiService.get('/NzPostal/get_address_details', address.address_id)
        .then(details => {
          if (type === 'postal') {
            this.customer.postal_address1 = details.address1;
            this.customer.postal_address2 = details.address2;
            this.customer.postal_suburb = details.suburb;
            this.customer.postal_state = details.state;
            this.customer.postal_postcode = details.postcode;
          } else {
            this.customer.address1 = details.address1;
            this.customer.address2 = details.address2;
            this.customer.suburb = details.suburb;
            this.customer.state = details.state;
            this.customer.postcode = details.postcode;
            this.customer.country_code = details.country_code;
            this.customer.latitude = details.latitude;
            this.customer.longitude = details.longitude;
          }
        }).finally(() => this.loading = false);
      },
      format: (address) => typeof address === 'string' ? address : '',
      search: (typed) => this.apiService.get('/NzPostal/get_addresses', typed)
        .then(addresses => addresses !== 'null' ? addresses : '')
    };

    this.billerSearch = {
      list: [],
      selected: null as any,
      choose: (customer) => {
        this.customer.biller_id = customer.id || null;
        this.customer.biller_name = customer.display_name || null;
      },
      format: (customer) => customer ? customer.display_name : '',
      search: (typed) => this.streamSearchService.search('customer_billers', typed)
        .then(customers => this.billerSearch.list.concat(customers))
    };

    if (this.customer.price_type === '5' && this.customer.price_matrix_id) {
      this.customer.price_type = this.customer.price_matrix_id;
    }

    if (this.customer.biller_id) {
      this.getBiller(this.customer.biller_id);
    }

    if (this.customer.country_code) {
      this.setCountryDefaults();
    }

    this.apiService.get('/system/country_types/', null, 'country_types').then(types => this.countryTypes = types);

    // Dealer branch data
    if (this.customerObj.isBranchCustomer?.()) {
      this.branchesApi.getCustomerPayments(this.customer.id)
        .then(payments => {
          this.branchPaymentsHO = payments.branch;
          this.branchPaymentsStores = payments.stores;
        }).catch(err => this.messagesService.error(err));

      this.branchesApi.getCustomerInvoices(this.customer.id)
        .then(invoices => this.branchInvoices = invoices)
        .catch(err => this.messagesService.error(err));
    }
  }

  get VVGarage () : any {
    return this.vvGarageFactory ? this.vvGarageFactory.create(this.customer) : null;
  }

  addCustomerToVVGarage () {
    this.VVGarage?.add_customer()
    .then(() => {
      this.messagesService.show(this.translateService.instant('VV_GARAGE_SPACE.CUSTOMER_ADDED_TO_VV_GARAGE'), 'success');
      this.router.stateService.reload();
    }).catch(err => this.messagesService.error(err));
  }

  archive () {
    const archived = !this.customer.archived;
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.ARCHIVE', {
      archived, name: this.customer.display_name
    }))
    .then(() => this.apiService.post('/customers/toggle_archived', { archived, customer_id: this.customer.id }))
    .then(() => {
      this.messagesService.show(this.translateService.instant('CUSTOMER_SPACE.ARCHIVED_SUCCESSFULLY', { archived }), 'success');
      this.router.stateService.reload();
    }).catch(err => this.messagesService.error(err));
  }

  createFromOtherBranch () {
    this.customerFactoryService.createCustomerFromOtherBranch(false)
    .then(results => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.CUSTOMER'), 'success');
      this.router.stateService.go('app.customer', { id: results.customer.id });
    });
  }

  delete () {
    this.customerObj.delete()
    .then(() => {
      this.router.stateService.go('app.customers');
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.CUSTOMER'), 'success');
    }).catch(err => this.messagesService.error(err));
  }

  emailStatement () {
    if (!this.customer.id) {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MUST_SAVE_CUSTOMER_BEFORE_EMAILING'), 'warning');
      return;
    }
    this.customerObj.emailStatement()
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.EMAIL_SENT'), 'success'));
  }

  getBiller (id: any) {
    this.apiService.get('/customer', id).then(customer => {
      this.billerSearch.selected = { display_name: customer.display_name, id: customer.id };
      this.customer.biller_name = customer.display_name;
    }).catch(err => this.messagesService.error(err));
  }

  print () {
    if (!this.customer.id) {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MUST_SAVE_CUSTOMER_BEFORE_PRINTING'), 'warning');
      return;
    }
    this.customerObj.printStatement();
  }

  removeCustomerFromVVGarage () {
    this.VVGarage?.delete_customer()
    .then(() => {
      this.messagesService.show(this.translateService.instant('VV_GARAGE_SPACE.CUSTOMER_REMOVED_FROM_VV_GARAGE'), 'success');
      this.router.stateService.reload();
    }).catch(err => this.messagesService.error(err));
  }

  save () {
    if (this.customer.tax_group_id) {
      this.customer.tax_group_name = this.taxGroups?.find(g => g.id === this.customer.tax_group_id)?.name;
    }

    const billerEl = document.getElementsByName('biller')[0] as HTMLInputElement;
    if (billerEl && billerEl.value === '') {
      this.customer.biller_id = null;
    }

    this.customerObj.save().then(customer => {
      this.customer = customer;
      this.customerObj = this.customerFactoryService.create(this.customer);
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.CUSTOMER'), 'success');
      this.router.stateService.go('app.customer', { id: this.customer.id }, { reload: true });
    }).catch(err => this.messagesService.error(err));
  }

  searchUKPostcode (type = 'customer') {
    const postcode = type === 'customer' ? this.customer.postcode : this.customer.postal_postcode;
    this.ukPostcodeApi.search(postcode).then(data => {
      if (type === 'postal') {
        this.customer.postal_address1 = data.Premises;
        this.customer.postal_address2 = data.Street;
        this.customer.postal_suburb = data.PostTown;
        this.customer.postal_postcode = data.Postcode;
      } else {
        this.customer.address1 = data.Premises;
        this.customer.address2 = data.Street;
        this.customer.suburb = data.PostTown;
        this.customer.postcode = data.Postcode;
      }
      this.messagesService.show(this.translateService.instant('CUSTOMER_SPACE.CUSTOMER_ADDRESS_UPDATED'), 'success');
    }).catch(err => this.messagesService.error(err));
  }

  sendEmail (email?: string) {
    this.customerObj.email(email || this.customer.email, this.companyMessages?.customer_email_message)
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'))
    .catch(err => this.messagesService.error(err));
  }

  sendPortalInvitation () {
    this.customerObj.sendPortalInvitation()
    .then(() => this.router.stateService.reload())
    .catch(err => this.messagesService.error(err));
  }

  sendSms () {
    this.customerObj.sms(this.customer)
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'))
    .catch(err => this.messagesService.error(err));
  }

  setCountryDefaults () {
    this.apiService.get('/system/states_for_country', this.customer.country_code).then(states => {
      this.states = states;
      return this.apiService.get('/system/country_validations', this.customer.country_code);
    }).then(validations => this.validations = validations)
    .catch(err => this.messagesService.error(err));
  }

  syncWithHeadOffice () {
    Promise.resolve()
    .then(() => this.customerObj.save())
    .then(() => this.apiService.patch('/customers/sync_multibranch_customer_entry', this.customer))
    .then(() => {
      this.messagesService.show(this.translateService.instant('MULTI_BRANCH_SPACE.CUSTOMER_LINKED'), 'success');
      this.router.stateService.reload();
    }).catch(err => this.messagesService.error(err));
  }

  checkRights (module: string): boolean { return this.sessionService.hasRights(module); }
  validateCompanyName (name: string) { return this.customerObj.validateCompanyName(name); }
  validateFirstAndLastName (name: string, field = 'first_name') { return this.customerObj.validateFirstAndLastName(name, field); }
}
